import React, { useState, useRef, useEffect } from "react";
import { TImages } from "models/inventory/interfaces/vehicle.interface";
import { ReactComponent as Arrow } from "design_system/src/static/icons/up-lg.svg";
import { ScalableImage } from "design_system/src";
import styles from "./Photos.module.scss";
import PhotosMobile from "./PhotosMobile";

interface PhotosProps {
  photos: TImages[];
  isMobile: boolean;
  showLenderImg?: boolean;
}

const Photos = ({ photos, isMobile, showLenderImg = false }: PhotosProps) => {
  if (photos.length === 0) return null;

  const magnifierWidth = window.innerWidth / 2 - 195;
  const magnifierHeight = 500;
  const zoom = 3.5;
  const showArrows = photos.length > 1;

  const [current, setCurrent] = useState(0);
  const [showMagnifier, setShowMagnifier] = useState(false);
  const [[imgWidth, imgHeight], setSize] = useState([0, 0]);
  const [[x, y], setXY] = useState([0, 0]);
  const [url, setUrl] = useState("");

  const thumbsContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!thumbsContainerRef.current) return;
    const thumbs = thumbsContainerRef.current.querySelectorAll("[data-thumb]");
    const activeThumb = thumbs[current] as HTMLElement | undefined;

    if (activeThumb) {
      activeThumb.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest",
      });
    }
  }, [current]);


  const onMouseEnter = (e: any, itemUrl: string) => {
    const element = e.currentTarget;
    const { width, height } = element.getBoundingClientRect();
    setSize([width, height]);
    setShowMagnifier(true);
    setUrl(itemUrl);
  };

  const onMouseLeave = (e: any) => {
    e.preventDefault();
    setShowMagnifier(false);
  };

  const onMouseMove = (e: any) => {
    const element = e.currentTarget;
    const { top, left } = element.getBoundingClientRect();
    const valueX = e.pageX - left - window.scrollX;
    const valueY = e.pageY - top - window.scrollY;
    setXY([valueX, valueY]);
  };

  const goTo = (idx: number) => setCurrent(idx);
  const prev = () => setCurrent((_prev) => (_prev === 0 ? photos.length - 1 : _prev - 1));
  const next = () => setCurrent((_prev) => (_prev === photos.length - 1 ? 0 : _prev + 1));

  const getMainUrl = (img: TImages) =>
    img.url || img.formats?.small?.url || img.formats?.thumbnail?.url || "";
  const getThumbUrl = (img: TImages) =>
    img.formats?.thumbnail?.url || img.url || "";

  if (isMobile) {
    return <PhotosMobile photos={photos} showLenderImg={showLenderImg} />;
  }

  return (
    <div className={styles.desktopSlider}>
      <div className={styles.thumbsCol} ref={thumbsContainerRef}>
        {photos.map((img, idx) => (
          <ScalableImage
            key={img._id}
            data-thumb
            className={`${styles.thumb}${idx === current ? ` ${styles.thumbActive}` : ""}`}
            src={getThumbUrl(img)}
            alt={img.name}
            desktopWidth={10}
            mobileWidth={10}
            onClick={() => goTo(idx)}
          />
        ))}
      </div>
      <div className={styles.mainImageWrapper}>
        <ScalableImage
          className={styles.mainImage}
          src={getMainUrl(photos[current])}
          alt={photos[current].name}
          desktopWidth={50}
          mobileWidth={50}
          onMouseEnter={(e) => onMouseEnter(e, getMainUrl(photos[current]))}
          onMouseLeave={(e) => onMouseLeave(e)}
          onMouseMove={(e) => onMouseMove(e)}
        />

        {showArrows && (
          <div className={styles.btnSection}>
            <button onClick={prev} type="button" className={styles.btn}>
              <Arrow className="text_primary_300" style={{ transform: "rotate(-90deg)" }} />
            </button>
            <button onClick={next} type="button" className={styles.btn}>
              <Arrow className="text_primary_300" style={{ transform: "rotate(90deg)" }} />
            </button>
          </div>
        )}

        {showLenderImg && (
          <div
            className="pos_absolute m_t_lg m_l_xxl"
            style={{ top: 0, left: 0, }}
          >
            <ScalableImage
              className="h_25_px"
              src="https://ik.imagekit.io/ozon/Homepage/Logos/logo_alianza_walmart_cashi_bodega_aurrera_xsmall"
              alt="Logo de cashi - walmart"
              desktopWidth={10}
              mobileWidth={10}
            />
          </div>
        )}
      </div>

      <div
        style={{
          display: showMagnifier ? "" : "none",
          position: "absolute",
          pointerEvents: "none",
          height: `${magnifierHeight}px`,
          width: `${magnifierWidth}px`,
          opacity: "1",
          backgroundImage: `url(${url})`,
          backgroundRepeat: "no-repeat",
          backgroundSize: `${imgWidth * zoom}px ${imgHeight * zoom}px`,
          backgroundPositionX: `${-x * zoom + magnifierWidth / 2}px`,
          backgroundPositionY: `${-y * zoom + magnifierHeight / 2}px`,
          top: 0,
          left: "103%",
          zIndex: 10,
        }}
      />
    </div>
  );
};

export default Photos;
